import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { Menu, X, User, Users } from 'lucide-react';
import { 
  LocationPinIcon, 
  HeartLuxuryIcon, 
  CalendarLuxuryIcon 
} from '@/components/icons/NavigationIcons';
import ConditionsMegaMenu from './ConditionsMegaMenu';
import TreatmentsMegaMenu from './TreatmentsMegaMenu';
import AboutDropdown from './AboutDropdown';

const LocalNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside or pressing escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  if (isMobile) {
    return (
      <>
        <div className={`fixed top-12 left-0 right-0 z-50 transition-all duration-400 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-xl shadow-apple-lg border-b border-juv-rose-clay/20' 
            : 'bg-white/90 backdrop-blur-lg'
        } py-4 px-6 safe-area-top`}>
          <div className="flex items-center justify-between">
            <Link to="/" className="text-center">
              <div className="text-xl font-bold text-juv-deep-chestnut font-serif tracking-wide">JUV</div>
              <div className="text-sm text-juv-deep-chestnut/80 font-light tracking-wider lowercase -mt-1">Medspa<sup className="text-xs">™</sup></div>
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-3 hover:bg-juv-soft-blush/20 rounded-xl transition-all duration-300 mobile-touch-target apple-hover-scale"
              aria-label="Toggle menu"
            >
              <Menu size={24} className="text-juv-deep-chestnut" />
            </button>
          </div>
        </div>

        {/* Enhanced Mobile Menu Overlay */}
        <div className={`mobile-slide-menu ${isMobileMenuOpen ? 'open' : 'closed'} safe-area-top safe-area-bottom`}>
          <div className="flex flex-col h-full">
            {/* Header with close button */}
            <div className="flex items-center justify-between p-6 border-b border-juv-rose-clay/20">
              <Link to="/" className="text-center" onClick={() => setIsMobileMenuOpen(false)}>
                <div className="text-xl font-bold text-juv-deep-chestnut font-serif tracking-wide">JUV</div>
                <div className="text-sm text-juv-deep-chestnut/80 font-light tracking-wider lowercase -mt-1">Medspa<sup className="text-xs">™</sup></div>
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-3 hover:bg-juv-soft-blush/20 rounded-xl transition-all duration-300 mobile-touch-target apple-hover-scale"
                aria-label="Close menu"
              >
                <X size={24} className="text-juv-deep-chestnut" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto py-8 px-6 apple-spacing-lg">
              {/* Conditions Section */}
              <div className="apple-spacing-sm">
                <h3 className="text-lg font-semibold mb-4 text-juv-deep-chestnut apple-body">Conditions</h3>
                <div className="apple-spacing-xs pl-4">
                  {[
                    { name: "Acne & Scars", href: "/conditions/acne-scars" },
                    { name: "Fine Lines & Wrinkles", href: "/conditions/fine-lines-wrinkles" },
                    { name: "Pigmentation & Spots", href: "/conditions/pigmentation-spots" },
                    { name: "Dull & Dehydrated Skin", href: "/conditions/dull-dehydrated-skin" },
                    { name: "Uneven Tone & Texture", href: "/conditions/uneven-tone-texture" },
                    { name: "Body Fat & Double Chin", href: "/conditions/body-fat-double-chin" }
                  ].map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block text-juv-deep-chestnut/80 hover:text-juv-terracotta py-3 apple-body transition-all duration-300 hover:translate-x-1 transform mobile-touch-target"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Treatments Section */}
              <div className="apple-spacing-sm">
                <h3 className="text-lg font-semibold mb-4 text-juv-deep-chestnut apple-body">Treatments</h3>
                <div className="apple-spacing-xs pl-4">
                  {[
                    { name: "Botox", href: "/treatments/botox" },
                    { name: "Lip Filler", href: "/treatments/lip-filler" },
                    { name: "Skin Boosters", href: "/treatments/skin-boosters" },
                    { name: "RF Microneedling", href: "/treatments/rf-microneedling" },
                    { name: "Laser Facial", href: "/treatments/laser-facial" },
                    { name: "IV Therapy", href: "/treatments/iv-therapy" }
                  ].map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block text-juv-deep-chestnut/80 hover:text-juv-terracotta py-3 apple-body transition-all duration-300 hover:translate-x-1 transform mobile-touch-target"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Main Navigation */}
              <div className="apple-spacing-sm">
                <div className="space-y-2">
                  <Link
                    to="/membership"
                    className="block text-lg font-medium text-juv-deep-chestnut hover:text-juv-terracotta transition-all duration-300 py-3 mobile-touch-target apple-body"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Membership
                  </Link>
                  <Link
                    to="/locations"
                    className="block text-lg font-medium text-juv-deep-chestnut hover:text-juv-terracotta transition-all duration-300 py-3 mobile-touch-target apple-body"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Locations
                  </Link>
                  <Link
                    to="/about"
                    className="block text-lg font-medium text-juv-deep-chestnut hover:text-juv-terracotta transition-all duration-300 py-3 mobile-touch-target apple-body"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    About
                  </Link>
                </div>
              </div>

              {/* Sign In Section */}
              <div className="border-t border-juv-rose-clay/20 pt-6 apple-spacing-sm">
                <h3 className="text-lg font-semibold mb-4 text-juv-deep-chestnut apple-body">Sign In</h3>
                <div className="space-y-3">
                  <Link
                    to="/login/patient"
                    className="flex items-center space-x-3 p-4 bg-juv-soft-blush/20 rounded-xl hover:bg-juv-soft-blush/30 transition-all duration-300 apple-hover-scale mobile-touch-target"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <User className="h-5 w-5 text-juv-terracotta" />
                    <span className="text-juv-deep-chestnut font-medium apple-body">Patient Portal</span>
                  </Link>
                  <Link
                    to="/login/staff"
                    className="flex items-center space-x-3 p-4 bg-juv-terracotta/10 rounded-xl hover:bg-juv-terracotta/20 transition-all duration-300 apple-hover-scale mobile-touch-target"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Users className="h-5 w-5 text-juv-terracotta" />
                    <span className="text-juv-deep-chestnut font-medium apple-body">Provider Portal</span>
                  </Link>
                  <Link
                    to="/partner"
                    className="block text-center py-2 px-4 text-sm text-juv-terracotta hover:text-juv-deep-chestnut transition-all duration-300 ml-8 apple-caption"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    → Become a Partner
                  </Link>
                </div>
              </div>
            </div>

            {/* Bottom CTAs */}
            <div className="p-6 border-t border-juv-rose-clay/20 space-y-4">
              <Link
                to="/quiz"
                className="apple-button-primary w-full text-center flex items-center justify-center space-x-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <HeartLuxuryIcon size={18} className="text-white" />
                <span>Take Glow Quiz</span>
              </Link>
              <Link
                to="/locations#book"
                className="apple-button-secondary w-full text-center flex items-center justify-center space-x-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <CalendarLuxuryIcon size={18} className="text-juv-terracotta" />
                <span>Book Now</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Backdrop */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className={`fixed top-12 left-0 right-0 z-50 transition-all duration-400 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-xl shadow-apple-lg border-b border-juv-rose-clay/20' 
        : 'bg-white/90 backdrop-blur-lg'
    } py-4 px-6`}>
      <div className="apple-container">
        <nav className="flex items-center justify-between">
          <Link to="/" className="flex items-center text-juv-deep-chestnut hover:text-juv-terracotta transition-all duration-300">
            <span className="text-2xl font-serif font-bold tracking-wide">JUV Medspa<sup className="text-sm">™</sup></span>
          </Link>

          <div className="flex items-center space-x-8">
            <ConditionsMegaMenu />
            <TreatmentsMegaMenu />
            
            <Link
              to="/membership"
              className="apple-nav-link"
            >
              Membership
            </Link>
            <Link
              to="/locations"
              className="apple-nav-link"
            >
              Locations
            </Link>
            
            <AboutDropdown />

            <div className="flex items-center space-x-3 border-l border-juv-rose-clay/20 pl-6">
              <Link
                to="/quiz"
                className="apple-button-primary flex items-center space-x-2"
              >
                <HeartLuxuryIcon size={18} className="text-white" />
                <span>Quiz</span>
              </Link>
              <Link
                to="/locations#book"
                className="apple-button-secondary flex items-center space-x-2"
              >
                <CalendarLuxuryIcon size={18} className="text-juv-terracotta" />
                <span>Book Now</span>
              </Link>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default LocalNavigation;
